import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { clearSiteSettingsCache } from "@/hooks/useSiteSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  Bell,
  Lock,
  Database,
  Sparkles,
  Globe,
  FileText,
  Loader2,
  Save,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import ManagersPanel from "@/components/ManagersPanel";

const FIELDS = [
  { key: "tagline", label: "Footer tagline", hint: "Shown under the logo in the footer.", type: "input" },
  { key: "contact_email", label: "Contact email", hint: "Public email in footer › Contact.", type: "input" },
  { key: "contact_address", label: "Contact address", hint: "Public location in footer › Contact.", type: "input" },
  { key: "copyright_text", label: "Copyright line", hint: "Bottom-left of footer.", type: "input" },
  { key: "version_label", label: "Version label", hint: "Bottom-right of footer.", type: "input" },
];

const LEGAL_FIELDS = [
  { key: "privacy_policy", label: "Privacy Policy (Markdown)", testid: "site-privacy-input" },
  { key: "terms_of_service", label: "Terms of Service (Markdown)", testid: "site-terms-input" },
];

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/site-settings")
      .then((r) => setForm(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/admin/site-settings", form);
      setForm(r.data);
      clearSiteSettingsCache();
      toast.success("Site content updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-8 max-w-4xl">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-500 dark:text-cyan-400 mb-2">
          / Settings
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Workspace Settings</h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
          Tune the command center to match your operation.
        </p>
      </div>

      <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40">
        <div className="flex items-center gap-3 mb-4">
          <SettingsIcon className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
          <h2 className="font-display text-lg font-semibold">Profile</h2>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-3">
            <span className="text-slate-500">Name</span>
            <span className="text-slate-900 dark:text-white">{user?.name}</span>
          </div>
          <div className="flex justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-3">
            <span className="text-slate-500">Email</span>
            <span className="text-slate-900 dark:text-white">{user?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Role</span>
            <span className="text-cyan-600 dark:text-cyan-300 font-mono-tech">{user?.role}</span>
          </div>
        </div>
      </section>

      <form
        onSubmit={onSave}
        className="border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40 space-y-6"
        data-testid="site-content-form"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
            <div>
              <h2 className="font-display text-lg font-semibold">Site Content & Legal</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Edits land on the public site and footer instantly.
              </p>
            </div>
          </div>
          <Button
            type="submit"
            disabled={saving || loading || !form}
            data-testid="site-content-save-btn"
            className="bg-cyan-500 text-slate-950 hover:bg-cyan-400 dark:bg-cyan-400 dark:hover:bg-cyan-300 font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>

        {loading || !form ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading site settings…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label
                    htmlFor={`site-${f.key}`}
                    className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400"
                  >
                    {f.label}
                  </Label>
                  <Input
                    id={`site-${f.key}`}
                    data-testid={`site-${f.key}-input`}
                    value={form[f.key] || ""}
                    onChange={(e) => update(f.key, e.target.value)}
                  />
                  {f.hint && <p className="text-[11px] text-slate-500">{f.hint}</p>}
                </div>
              ))}
            </div>

            <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                <span className="text-sm font-display font-semibold">Legal documents</span>
                <span className="text-[11px] text-slate-500">
                  Markdown supported · # heading · **bold** · - list · [link](https://…)
                </span>
              </div>
              <div className="space-y-5">
                {LEGAL_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label
                      htmlFor={`site-${f.key}`}
                      className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400"
                    >
                      {f.label}
                    </Label>
                    <Textarea
                      id={`site-${f.key}`}
                      data-testid={f.testid}
                      rows={14}
                      className="font-mono text-sm leading-relaxed"
                      value={form[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </form>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Bell, title: "Notifications", text: "Coming in Phase 2 — alerts when packages stall or get stuck." },
          { icon: Lock, title: "Security", text: "Coming in Phase 2 — password rotation, MFA, session control." },
          { icon: Database, title: "API & Webhooks", text: "Coming in Phase 2 — connect ERPs, POS, and 3PLs." },
        ].map((c) => (
          <div
            key={c.title}
            className="border border-slate-200 dark:border-slate-800 rounded-lg p-5 bg-slate-100/40 dark:bg-slate-900/40"
          >
            <c.icon className="w-5 h-5 text-cyan-500 dark:text-cyan-400 mb-3" />
            <div className="font-display font-semibold mb-1">{c.title}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{c.text}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-wider text-yellow-500 dark:text-yellow-400 border border-yellow-500/30 bg-yellow-500/5 rounded-full px-2 py-0.5">
              <Sparkles className="w-3 h-3" /> Phase 2
            </div>
          </div>
        ))}
      </div>

      <ManagersPanel
        role="Sub-Admin"
        apiBase="/admin/sub-admins"
        testIdRoot="sub-admin"
      />

      <DangerZone />
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const PHRASE = "DELETE ALL DEMO DATA";
  const ready = password.length > 0 && confirm === PHRASE && !busy;

  const wipe = async () => {
    if (!ready) return;
    if (!window.confirm(
      "FINAL WARNING: This will permanently delete ALL packages, QR codes, stores, riders, dispatchers, warehouses, and history. Your super-admin account will be the only thing left.\n\nProceed?"
    )) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/wipe-demo-data", { password, confirm });
      setResult(data);
      toast.success(`Wiped ${data.total_deleted} records.`);
      setPassword("");
      setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e) || "Wipe failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="danger-zone"
      className="mt-8 border-2 border-red-500/30 bg-red-500/5 rounded-lg p-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <h3 className="font-display font-semibold text-red-300">Danger Zone</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4 leading-relaxed">
        Irreversible actions. Use these only when you intend to permanently destroy data.
      </p>

      {!open ? (
        <Button
          data-testid="wipe-demo-open-btn"
          variant="outline"
          onClick={() => setOpen(true)}
          className="bg-slate-900 border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Wipe all demo / test data
        </Button>
      ) : (
        <div className="space-y-4 max-w-xl">
          <div className="border border-red-500/30 bg-red-950/40 rounded-md p-3 text-xs text-red-200 leading-relaxed">
            This will permanently delete <b>all</b> packages, QR codes, items, products,
            stores, store dispatchers, riders, warehouses, warehouse dispatchers, batches,
            history, push subscriptions, and all non-super-admin users. <b>Your super-admin
            account is preserved.</b> Action cannot be undone.
          </div>

          <div>
            <Label htmlFor="wipe-password" className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">
              Re-enter your password
            </Label>
            <Input
              id="wipe-password"
              data-testid="wipe-demo-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
              placeholder="Your super-admin password"
            />
          </div>

          <div>
            <Label htmlFor="wipe-confirm" className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">
              Type <span className="text-red-300">{PHRASE}</span> to confirm
            </Label>
            <Input
              id="wipe-confirm"
              data-testid="wipe-demo-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white font-mono-tech"
              placeholder={PHRASE}
            />
          </div>

          <div className="flex gap-3">
            <Button
              data-testid="wipe-demo-confirm-btn"
              disabled={!ready}
              onClick={wipe}
              className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {busy ? "Wiping…" : "Wipe everything now"}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => { setOpen(false); setPassword(""); setConfirm(""); }}
              className="text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
          </div>

          {result && (
            <div data-testid="wipe-demo-result" className="border border-green-500/30 bg-green-500/5 rounded-md p-3 text-xs text-green-200">
              <div className="font-semibold mb-1.5">Wipe complete — {result.total_deleted} records removed</div>
              <ul className="space-y-0.5 font-mono-tech text-[11px]">
                {Object.entries(result.by_collection || {}).map(([k, v]) => (
                  <li key={k}><span className="text-slate-400">{k}:</span> {v}</li>
                ))}
              </ul>
              <div className="mt-2 text-slate-300">Refresh any dashboards to see the empty state.</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
